/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import MapStylesIcon from '@ion/dls/src/icons/current/MapStylesIcon';
import {MapViewIcon} from '@ion/dls/src/icons/MapViewIcon';
import {MapToolPill} from '@ion/dls/src/maps/toolbar/MapToolPill';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';

// Defines the props for MapViewTool component.
export type MapViewToolProps = {
  onClick: React.MouseEventHandler;
  size?: IconSize;
  isSelected?: boolean;
  isAdminGeofence?: boolean;
}

/**
 * The MapViewTool component provides the UI which displays the associated Icon
 * button within a Simple Map that, when clicked, initiates the corresponding
 * feature.
 *
 * This MapViewTool represents the "Maps Tool" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <MapViewTool size={IconSize.large} isSelected={isSelected} onClick={function() {}} />
 *
 * ```
 */
export const MapViewTool = ({
  onClick,
  size = IconSize.medium,
  isSelected = false,
  isAdminGeofence = false
}: MapViewToolProps) => {
  const isPhone     = useIsPhone();
  const {isCurrent} = useThemeContext();

  return (isAdminGeofence && isCurrent) ? (
    <MapToolPill icon={<MapStylesIcon size={IconSize.medium} />}
      label={translate('maps:controls.mapStyles')}
      isSelected={isSelected}
      onClick={onClick} />
  ) : (
    <FloatingIconButtonGroup>
      <Tooltip title={translate('maps:controls.view')} renderOnlyChildren={isPhone}>
        <div>
          <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}
            isSelected={isSelected}>
            <MapViewIcon size={size} />
          </FloatingIconButtonGroup.FloatingIconButton>
        </div>
      </Tooltip>
    </FloatingIconButtonGroup>
  );
};

export default MapViewTool;
